import { A } from '@adornis/base/env-info.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { Observable } from 'rxjs';
import { KPF } from './master-data-management/kpf/entities/KPF.js';
import { ChancenwerkUser } from './master-data-management/users/entities/ChancenwerkUser.js';

/**
 * @entity AcquisitionRecordDuplicates
 */
@Entity()
export class AcquisitionRecordDuplicates extends AdornisEntity {
  public static override _class = 'AcquisitionRecordDuplicates';

  @Field(() => ID, {
    default: v => {
      return v ?? A.getGloballyUniqueID();
    },
  })
  public _id: Maybe<string>;

  @Field(() => Float, { default: v => v ?? 0 })
  public probabilityOfDuplicate: Maybe<number>;

  @Field(() => ID)
  public entityID: Maybe<string>;

  @Field(() => ID)
  public duplicateID: Maybe<string>;

  @Field(() => String)
  public duplicateCollection: Maybe<string>;

  // The duplicate search matches employees as well as students (see searchForDuplicateForUserData),
  // so this must be the common user supertype - typing it as `Student` made GraphQL reject every
  // employee hit with `Runtime Object type "ExternerMitarbeiter" is not a possible type for "StudentInterface"`.
  @Field(() => ChancenwerkUser, {
    resolve: function (this: AcquisitionRecordDuplicates) {
      return async () => {
        if (!this.duplicateID || !this.duplicateCollection) return null;
        const collection = await getRawCollection<EntityData<ChancenwerkUser>>(this.duplicateCollection);
        return constructValue(await collection.findOne({ _id: this.duplicateID }));
      };
    },
  })
  public duplicate: Maybe<ChancenwerkUser>;
}

/**
 * @entity AcquisitionRecordFilter
 */
@Entity()
export class AcquisitionRecordFilter extends AdornisFilter {
  public static override _class = 'AcquisitionRecordFilter';

  constructor(doc: PartialEntityData<AcquisitionRecordFilter>) {
    super(doc);
  }

  @Field(() => String, { default: v => v ?? null })
  public schoolID!: string | null;

  @Field(() => Boolean, { default: v => v ?? null })
  public hasStudents!: boolean | null;

  override getMongoFilter(): Dictionary<any> {
    return { $and: [{ _class: 'AcquisitionRecord' }, ...this.getFilterArray()] };
  }

  getFilterArray(): Dictionary<any>[] {
    const filter = this.toObject();

    const schoolIDFilter = filter.schoolID ? { schoolID: filter.schoolID } : {};
    const hasStudentsFilter = filter.hasStudents ? { 'createdStudents.0': { $exists: true } } : {};

    return [schoolIDFilter, hasStudentsFilter];
  }
}

/**
 * @entity AcquisitionRecord
 */
@Entity()
export class AcquisitionRecord extends MongoEntity {
  public static override _class = 'AcquisitionRecord';
  public static override _collectionName = 'acquisition-record';

  constructor(doc: PartialEntityData<AcquisitionRecord>) {
    super(doc);
  }

  @Field(() => String)
  public kpfID: Maybe<string>;

  @DataLoadedField(() => KPF, ({ kpfID }) => kpfID)
  public kpf: Maybe<KPF>;

  @Field(() => [String], { default: v => v ?? [] })
  public createdStudents?: string[];

  @Field(() => [AcquisitionRecordDuplicates], { default: v => v ?? [] })
  public duplicates: Maybe<AcquisitionRecordDuplicates[]>;

  @Query(() => AcquisitionRecord)
  static getAcquisitionRecordForKPF(@Arg('kpfID', () => String) kpfID: string) {
    return async (gqlFields: BaseQLSelectionSet<AcquisitionRecord>) => {
      if (!kpfID) return null;

      const collection = await getCollection(AcquisitionRecord._class);
      const record = (await collection.findOne({ kpfID })) as AcquisitionRecord | null;

      if (!record) {
        const newRecord = new AcquisitionRecord({ kpfID });

        const id = await newRecord.create();
        newRecord._id = id;
        return newRecord;
      }
      return record;
    };
  }

  @Subscription(() => [AcquisitionRecord])
  public static subscribeAcquisitionRecordsByFilterPaginated(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filterObject', () => AcquisitionRecordFilter) filter: AcquisitionRecordFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<AcquisitionRecord>) => {
      return AcquisitionRecord.subscribePaginated<AcquisitionRecord>(skip, limit, filter.getMongoFilter(), {
        ...filter.getMongoOptions().sort,
      }) as Observable<AcquisitionRecord[]>;
    };
  }
}
